import React from 'react';

import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { Button, Checkbox, FormControlLabel, GridLegacy as Grid, MenuItem } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import LereService from '~/services/LereService';

import { hasFullAccess } from '~/utils/lere';

import { Select } from '../Common/Forms/fields';

import { LereTabComponentProps } from './types';

const emailVariantMappings = {
  tpfm_daily: 'DAILY_TPFM',
  tpfm_weekly: 'TPFM',
  fulltrip_post_purchase: 'POST_PURCHASE',
  perfect_match: 'PERFECT_MATCH',
  smoke_daily_tpfm: 'SMOKE_TPFM_DAILY',
  smoke_weekly_tpfm: 'SMOKE_TPFM_WEEKLY',
  smoke_tour_rec: 'SMOKE_TOUR_REC',
  tour_rec: 'TOUR_RECOMMENDATIONS',
};
const testRecipients = {
  'shay.hamama@luxuryescapes.com': 'b4442932-9f78-48d5-86bf-c0d9442f67ca',
  'samuel.drown@luxuryescapes.com': '0386d4bc-43f1-4f1a-ada0-c667c3dbffe4',
  'ryan.tian@luxuryescapes.com': 'a3d9d6e1-e013-4fbc-a0b5-c12f1ca5f914',
  'nirnay.polaboina@luxuryescapes.com': '201ee013-e557-4635-bd47-442a828108b7',
  'vladislav.milov@luxuryescapes.com': '974510cf-d848-4638-b2ef-b28058d5c4d0',
  'ahijith.chandra@luxuryescapes.com': 'cdaaf523-b676-481d-a2c1-3583eeac290c',
  'maria.nouneva@luxuryescapes.com': '61ac3878-01ae-4e1b-bcc0-90fb9b0d33ce',
  'hunter.li@luxuryescapes.com': 'a62112ee-19ef-4f12-9771-a51bc461e681',
  'christopher.nassif@luxuryescapes.com': '3b588c78-05b0-4330-ba90-1204d3ca5d2b',
  'lewis.bandas@luxuryescapes.com': '4a902447-0ab8-4c5c-aa6f-7135da31423a',
  'marcel.qayoomtaylor@luxuryescapes.com': '265d26c6-c7ca-4c64-b769-13cfae20d31d',
};

interface FormInput {
  emailVariant: string;
  recipient: string;
  reuseLastRecipientEmail: boolean;
}

export default function LereTPFMEmailTrigger({ onLereError }: LereTabComponentProps) {
  const { user } = useCurrentUser();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormInput>({
    defaultValues: {
      emailVariant: Object.keys(emailVariantMappings)[0],
      recipient: user.email,
      reuseLastRecipientEmail: false,
    },
  });
  testRecipients[user.email] = user.memberId;
  const possibleRecipients = Object.keys(testRecipients);

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    const { emailVariant, recipient, reuseLastRecipientEmail } = data;
    try {
      await LereService.sendTPFMTestEmail(
        emailVariantMappings[emailVariant],
        testRecipients[recipient],
        reuseLastRecipientEmail,
      );
    } catch (e) {
      onLereError(e);
    }
  };

  const userHasFullAccess = hasFullAccess(user);
  if (!userHasFullAccess) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1} style={{ marginBottom: '30px' }}>
        <Grid item xs={12}>
          <h2>Email Test Trigger</h2>
          <p>
            Send Test Emails. If 'Reuse Last Recipient Email' is left unchecked random user data will be used to
            generate the contents of the email.
          </p>
        </Grid>
        <Grid item md={6} flexGrow={1}>
          <Select control={control} name="emailVariant" muiLabel="Email Variant" disabled={isSubmitting}>
            {Object.keys(emailVariantMappings).map((variant) => (
              <MenuItem key={variant} value={variant}>
                {variant}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={6} flexGrow={1}>
          <Select control={control} disabled={isSubmitting} name="recipient" muiLabel="Email Recipient (Tester)">
            {possibleRecipients.map((recipient) => (
              <MenuItem key={recipient} value={recipient}>
                {recipient}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="reuseLastRecipientEmail"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} disabled={isSubmitting} />}
                label="Reuse Last Recipient Email"
              />
            )}
          />
        </Grid>
        <Grid item xs={2.5}>
          <Button
            disabled={isSubmitting}
            type="submit"
            size="large"
            color="primary"
            variant="contained"
            fullWidth
            style={{ marginTop: '5px' }}
          >
            Send
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
